import React, {Component} from "react";
import {Table, Icon, Button, Input, Slider} from "antd";
import {extent as d3Extent} from "d3-array";
import {
	FilterIcon,
	FilterStatusCount, SpinIcon,
	TableCellWithHighlighting,
	TableColumnTitleCell,
} from "../utilsJSX";
import {smartDisplayValue} from "../utilsDisplay";
import {numSort, strSort} from "../../utilsData";

const SLIDER_STEP_SIZE = 1000
const CELL_WIDTH = 134

const TOTAL_FUNDING_AMOUNT_FIELD = 'total_funding_amount'

const roundUp = (num, stepSize = 1000) => Math.ceil(num / stepSize) * stepSize
const roundDown = (num, stepSize = 1000) => Math.floor(num / stepSize) * stepSize

class MoneySlider extends React.Component {
	
	tooltipFormatter = (num) => `$${smartDisplayValue(num)}`
	
	render() {
		const {dataExtent, onAfterChange} = this.props
		return (
			<div>
				<Slider
					min={roundDown(dataExtent[0], SLIDER_STEP_SIZE)}
					max={roundUp(dataExtent[1], SLIDER_STEP_SIZE)}
					defaultValue={dataExtent}
					tipFormatter={this.tooltipFormatter}
					onAfterChange={onAfterChange}
					step={SLIDER_STEP_SIZE}
					range />
			</div>
		);
	}
}


export default class USASpendingDataTable extends Component {
	constructor(props) {
		super(props)
		
		this.state = {
			searchText: '',
			searchedColumn: '',
			filterBounds: null,
			filteredRows: props.usaSpendingData.rows,
		}
	}
	
	
	getColumnSearchProps = dataIndex => ({
		filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
			if (dataIndex === TOTAL_FUNDING_AMOUNT_FIELD) {
				return (
					<div style={{ padding: 8, width: 300 }}>
						<MoneySlider
							ref={node => {
								this.searchInput = node;
							}}
							onAfterChange={(filterBounds) => {
								this.setState({
									filterBounds: filterBounds,
									searchedColumn: dataIndex,
									filteredRows: this.filterDataBasedOnSearch(dataIndex, null, filterBounds)
								})
							}}
							dataExtent={this.state.dataExtent}
						/>
						<Button onClick={() => this.handleReset(clearFilters)} size="small" style={{ width: 90 }}>
							Clear
						</Button>
					</div>
				)
			}
			
			return (
				<div style={{ padding: 8 }}>
					<Input
						ref={node => {
							this.searchInput = node;
						}}
						placeholder={`Search ${dataIndex}`}
						value={selectedKeys[0]}
						onChange={e => {
							const newSearchTextArray = e.target.value ? [e.target.value] : []
							const newSearchText = newSearchTextArray[0]
							setSelectedKeys(newSearchTextArray)
							this.setState({
								searchText: newSearchText,
								searchedColumn: dataIndex,
								filteredRows: this.filterDataBasedOnSearch(dataIndex, newSearchText)
							})
						}}
						onPressEnter={() => this.handlePressEnter(selectedKeys, confirm, dataIndex)}
						style={{ width: 188, marginBottom: 8, display: 'block' }}
					/>
					<Button onClick={() => this.handleReset(clearFilters)} size="small" style={{ width: 90 }}>
						Clear
					</Button>
				</div>
			)
		},
		filterIcon: filtered => (
			<Icon type="search" style={{ color: filtered ? '#1890ff' : undefined }} />
		),
		onFilter: (value, record) =>
			record[dataIndex]
				.toString()
				.toLowerCase()
				.includes(value.toLowerCase()),
		onFilterDropdownVisibleChange: visible => {
			if (dataIndex !== TOTAL_FUNDING_AMOUNT_FIELD && visible) {
				setTimeout(() => this.searchInput.select());
			}
		},
		render: text => {
			const isBeingFiltered = this.state.searchedColumn === dataIndex
			const textToHighlight = ((isBeingFiltered && this.state.searchText) || '').toString()
			return TableCellWithHighlighting(text, textToHighlight)
		},
	});
	
	handlePressEnter = (selectedKeys, confirm, dataIndex) => {
		confirm();
	};
	
	handleReset = clearFilters => {
		clearFilters();
		this.setState({ searchText: '', filterBounds: null, filteredRows: this.props.usaSpendingData.rows });
	};
	
	filterDataBasedOnSearch = (searchedColumn, searchText = false, filterBounds = false) => this.props.usaSpendingData.rows.filter(record => {
		if (filterBounds) {
			const [lowerBound, upperBound] = filterBounds
			return lowerBound <= Number(record[searchedColumn]) && Number(record[searchedColumn]) <= upperBound
		}
		
		return !searchText || record[searchedColumn]
			.toString()
			.toLowerCase()
			.includes(searchText.toLowerCase())
	})
	
	componentDidUpdate(prevProps, prevState, snapshot) {
		const {usaSpendingData} = this.props
		const componentUpdatedToNewData = prevProps.usaSpendingData !== usaSpendingData
		if (componentUpdatedToNewData) {
			const newlyArrivedRows = usaSpendingData.rows
			this.setState({
				filteredRows: newlyArrivedRows,
				dataExtent: d3Extent(newlyArrivedRows, record => Number(record[TOTAL_FUNDING_AMOUNT_FIELD])),
			})
		}
	}
	
	
	render() {
		const {rows, fields} = this.props.usaSpendingData
		if (!this.state.filteredRows) return (<SpinIcon large/>)
		
		const USA_SPENDING_COLUMNS_TO_FIX_TO_LEFT = ['ppp_st_co']
		const USA_SPENDING_LINK_FIELD = 'usaspending_link'
		const columns = fields && Object.keys(fields).map(fieldName => ({
			title: () => <TableColumnTitleCell fieldName={fieldName} />,
			dataIndex: fieldName,
			key: fieldName,
			width: CELL_WIDTH,
			fixed: USA_SPENDING_COLUMNS_TO_FIX_TO_LEFT.includes(fieldName) ? 'left' : undefined,
			textWrap: "ellipsis",
			// wordWrap: "break-word",
			sorter: fields[fieldName].type === "string"
				? (a, b) => strSort(a, b, fieldName)
				: (a, b) => numSort(a, b, fieldName),
			...this.getColumnSearchProps(fieldName)
		}))
		const numNonFixedFields = Object.keys(fields).length - USA_SPENDING_COLUMNS_TO_FIX_TO_LEFT.length
		const aFilterIsActive = this.state.searchText || this.state.filterBounds
		
		return (
			<div>
				<USASpendingFilterStatus filteredRows={this.state.filteredRows} rows={rows} aFilterIsActive={aFilterIsActive} />
				<Table
					columns={columns}
					dataSource={this.state.filteredRows}
					scroll={{ x: CELL_WIDTH * (numNonFixedFields + 2), y: undefined }}
					rowKey={USA_SPENDING_LINK_FIELD}
					size="small"
				/>
			</div>
		)
	}
}

const USASpendingFilterStatus = ({filteredRows, rows, aFilterIsActive}) => (
		<div className="usa-spending-filter-status">
			<FilterStatusCount activeData={filteredRows} totalData={rows}/>
			{` of federal grants `}
			{aFilterIsActive ? <span>{`match your filter `}<FilterIcon/></span> : <span>{`(click a column's `}<FilterIcon/>{` to filter the data)`}</span>}
		</div>
)
